<template>
  <article
    class="platform-card"
    :class="{ 'is-selectable': selectable, 'is-selected': selectable && selected }"
    :role="selectable ? 'button' : null"
    :tabindex="selectable ? 0 : null"
    :aria-pressed="selectable ? String(selected) : null"
    @click="onCardClick"
    @keydown="onCardKeydown"
  >
    <div class="platform-card__body">
      <header class="platform-card__head">
        <FormCheckbox
          v-if="selectable"
          class="platform-card__check"
          size="small"
          :value="selected"
          @click.native.stop
          @input="$emit('select', $event)"
        />
        <PlatformIcon :platform="platform.id" />
        <h3 class="platform-card__name">{{ nameOf(platform) }}</h3>
      </header>

      <a
        v-if="urlOf(platform)"
        class="platform-card__url"
        :href="urlOf(platform)"
        target="_blank"
        rel="noopener noreferrer"
        v-tooltip="{ content: urlOf(platform), html: false, placement: 'top', delay: 400 }"
        @click.stop="onUrlClick()"
      >
        <span class="platform-card__url-host">{{ hostOf(urlOf(platform)) }}</span>
        <BaseIcon name="external-link" />
      </a>

      <div v-if="regions.length || tags.length" class="platform-card__tags">
        <BaseTag v-for="code in regions" :key="`region-${code}`" tone="neutral" preset="soft">
          {{ code }}
        </BaseTag>
        <BaseTag
          v-for="tag in tags"
          :key="`tag-${tag}`"
          :tone="platformTagTone(tag)"
          :icon="platformTagIcon(tag)"
          preset="soft"
        >
          {{ $t('tags.' + tag, displayLocale) }}
        </BaseTag>
      </div>

      <p v-if="descriptionOf(platform)" class="platform-card__desc">
        {{ descriptionOf(platform) }}
      </p>
    </div>

    <div v-if="subPlatforms.length" class="platform-card__section">
      <button
        type="button"
        class="platform-card__expand"
        :aria-expanded="String(expanded)"
        @click.stop="expanded = !expanded"
      >
        <span>{{ $t('search-filters.platform-filter.see-subplatforms', displayLocale) }}</span>
        <BaseIcon :name="expanded ? 'chevron-up' : 'chevron-down'" />
      </button>
      <ul v-if="expanded" class="platform-card__subs">
        <li v-for="sub in subPlatforms" :key="sub.id || sub.nameEn || sub.nameFr" class="platform-card__sub">
          <div class="platform-card__sub-body">
            <span class="platform-card__sub-name">{{ nameOf(sub) }}</span>
            <a
              v-if="urlOf(sub)"
              class="platform-card__url is-sub"
              :href="urlOf(sub)"
              target="_blank"
              rel="noopener noreferrer"
              v-tooltip="{ content: urlOf(sub), html: false, placement: 'top', delay: 400 }"
              @click.stop="onUrlClick(sub)"
            >
              <span class="platform-card__url-host">{{ hostOf(urlOf(sub)) }}</span>
              <BaseIcon name="external-link" />
            </a>
            <p v-if="descriptionOf(sub)" class="platform-card__sub-desc">
              {{ descriptionOf(sub) }}
            </p>
          </div>
        </li>
      </ul>
    </div>
  </article>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag';
import FormCheckbox from '@/components/form/FormCheckbox';
import PlatformIcon from '@/components/PlatformIcon';
import { getPlatformTagIcon, getPlatformTagTone } from '@/util/tag-tones/platform-tags';
import { LOCALE } from '@/util/i18n/locale';
import { hostOf, isWebUrl } from '@/util/url-util';

/**
 * A platform as users see it in the platforms detail panel: name, site link, region codes and tags,
 * description in the display locale, expandable sub-platforms. With `selectable`, the whole card
 * toggles the selection and emits `select`(checked). The admin catalog renders it read-only as a
 * live preview, in either locale.
 */
export default {
  name: 'PlatformCard',
  components: { BaseIcon, BaseTag, FormCheckbox, PlatformIcon },
  props: {
    platform: { type: Object, required: true },
    selectable: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    // Defaults to the app locale.
    locale: { type: String, default: null },
  },
  data() {
    return { expanded: false };
  },
  computed: {
    displayLocale() {
      return this.locale || this.$i18n.locale;
    },
    regions() {
      const regions = Array.isArray(this.platform.regions) ? this.platform.regions : [];
      return regions.map((region) => (typeof region === 'string' ? region : region && region.code)).filter(Boolean);
    },
    tags() {
      return this.platform.tags || [];
    },
    subPlatforms() {
      return this.platform.subplatforms || this.platform.subPlatforms || [];
    },
  },
  methods: {
    platformTagTone: getPlatformTagTone,
    platformTagIcon: getPlatformTagIcon,
    hostOf,
    // The API stores urls unchecked, and the admin preview shows a draft as it's typed.
    urlOf(platform) {
      return isWebUrl(platform.url) ? platform.url : '';
    },
    onUrlClick(sub) {
      const id = sub ? `${this.platform.id}/${sub.id}` : this.platform.id;
      this.$track.platformExternalLink(id);
    },
    isFrench() {
      return this.displayLocale === LOCALE.FR_CA;
    },
    nameOf(platform) {
      return this.isFrench() ? platform.nameFr : platform.nameEn;
    },
    descriptionOf(platform) {
      const description = platform.description || {};
      return this.isFrench()
        ? platform.descriptionFr || description.fr || ''
        : platform.descriptionEn || description.en || '';
    },
    onCardClick() {
      if (this.selectable) this.$emit('select', !this.selected);
    },
    onCardKeydown(event) {
      // Only when the card itself has focus: Enter on the sub-platforms button must not toggle it.
      if (!this.selectable || event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.$emit('select', !this.selected);
      }
    },
  },
};
</script>

<style lang="scss">
.platform-card {
  display: flex;
  flex-direction: column;
  // Fits a collapsed card's usual content (site link, four-line description, sub-platforms toggle),
  // so cards in a row line up: the platforms grid doesn't stretch them.
  min-height: 210px;
  background: $color-white;
  border: 1px solid $color-border;
  border-radius: $border-radius-root;
  padding: 16px 18px;

  &.is-selectable {
    cursor: pointer;
    transition: border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease;

    &:hover {
      border-color: $color-accent-300;
      background: $color-accent-100;
      box-shadow: $shadow-sm;
    }

    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px $color-accent;
    }
  }

  &.is-selected {
    border-color: $color-accent-300;
    box-shadow: inset 0 0 0 1px $color-accent-300;
    background: $color-accent-100;

    &:hover {
      background: $color-accent-200;
    }
  }

  &__body {
    flex: 1 1 auto;
    min-height: 0;
  }

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }

  &__check {
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    margin-right: 0;
  }

  &__name {
    margin: 0;
    font-family: $font-family-raleway;
    font-size: 16px;
    font-weight: 800;
    color: $color-normal;
    line-height: 1.3;
    min-width: 0;
  }

  &__url {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: 100%;
    margin: -4px 0 10px;
    color: $color-accent;
    font-size: 13px;
    text-decoration: none;

    &:hover .platform-card__url-host {
      text-decoration: underline;
    }

    .feather {
      flex: 0 0 auto;
      width: 12px;
      height: 12px;
    }

    &.is-sub {
      margin: 2px 0 0;
      font-size: 12px;
    }
  }

  &__url-host {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 10px;
  }

  &__desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
    color: $color-text-body;
    white-space: pre-wrap;
  }

  &__section {
    flex: 0 0 auto;
    margin-top: auto;
    padding-top: 12px;
  }

  &__expand {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    font-weight: bold;
    color: $color-accent;
    position: relative;
    z-index: 1;

    .feather {
      width: 14px;
      height: 14px;
    }

    &:hover {
      text-decoration: underline;
    }
  }

  &__subs {
    list-style: none;
    margin: 10px 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__sub {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    background: $color-neutral-pale;
    border-radius: $radius-sm;
  }

  &__sub-body {
    min-width: 0;
  }

  &__sub-name {
    display: block;
    font-size: 13px;
    font-weight: bold;
    color: $color-normal;
  }

  &__sub-desc {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.4;
    color: $color-slate-500;
  }
}
</style>
